import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { IOrganisationsHierarchy, OrganisationDataService, OrganisationsHierarchy } from '@services/organisation-data.service';
import { SelectEventArgs } from '@syncfusion/ej2-angular-dropdowns';
import { ActivatedRoute, Router } from '@angular/router';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { GlobalFunctions, imagePath, imgTypes } from '@global/GlobalFunctions';
import { distinctUntilChanged, filter, finalize, takeUntil } from 'rxjs/operators';
import { combineLatest, Subject } from 'rxjs';
import { LevelAsset, AssetsDataService, ILevelAsset } from '@services/assets-data.service';
import { MatDialog } from '@angular/material/dialog';
import { EditHierarchyLocationComponent } from '../../../dialogs/edit-hierarchy-location/edit-hierarchy-location.component';
import { EditHierarchyAssetComponent } from '../../../dialogs/edit-level-asset/edit-level-asset.component';
import { AuthenticationService, IWebAppUser } from '@services/authentication.service';

/**
 * Default Class
 */
@Component({
	selector: 'app-hierarchy-location',
	templateUrl: './hierarchy-location.component.html',
	styleUrls: ['./hierarchy-location.component.scss']
})
export class HierarchyLocationComponent implements OnInit, OnDestroy {

	/** Path to Sub Location Images */
	public imagePathSubLocations(): string {
		return imagePath(this.accountID, imgTypes.sublocations).toLowerCase()
	}
	/** child */
	@Input() child: OrganisationsHierarchy;

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Route Asset Parameter */
	public hierarchicalLocation?: OrganisationsHierarchy

	/** Asset Children */
	public locationChildren: OrganisationsHierarchy[]

	/** Assets List */
	public locationAssets: LevelAsset[] = [];

	/** set the Selected nodes to the TreeView */
	public locationParents: OrganisationsHierarchy[];

	/** Selected Text */
	public selectedLocation: string = ""

	/** Data to Tree View */
	public hierarchicalData: OrganisationsHierarchy[]

	/** Fields */
	public fields: any

	/** Child Path */
	public childPath: string = ""

	/** Navigation area Is Colapsed */
	public isCollapsed = false;

	/** accountID */
	public accountID: string
	/** OrganisationID */
	public OrganisationID: string

	/** this user */
	public thisUser: IWebAppUser

	/**
	 *  Default Constructor
	 * @param organisationDataService Organisation Data
	 * @param assetsDataService AssetsDataService
	 * @param router router
	 * @param route route
	 * @param siteSubscriptions site Subscriptions
	 * @param globalFunctions global Functions
	 * @param dialog dialog
	 * @param authenticationService authenticationService
	 */
	constructor(
		public organisationDataService: OrganisationDataService,
		public assetsDataService: AssetsDataService,
		private router: Router,
		private route: ActivatedRoute,
		private siteSubscriptions: siteSubscriptions,
		private globalFunctions: GlobalFunctions,
		public dialog: MatDialog,
		private authenticationService: AuthenticationService
	) {

		combineLatest([
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([currentUser, selectedAccountGUID, selectedOrganisationUID, paramMap]) =>
				!!currentUser && !!selectedAccountGUID && !!selectedOrganisationUID && paramMap.has('sublocation'))
		).subscribe({
			next: ([currentUser, selectedAccountGUID, selectedOrganisationUID, paramMap]) => {
				this.thisUser = <IWebAppUser>currentUser
				this.accountID = selectedAccountGUID;
				this.OrganisationID = selectedOrganisationUID;
				var paramSublocation = paramMap.get('sublocation') ?? "";
				this.subscribeHierarchicalData(paramSublocation)
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});

	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void { }

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		} catch { }
	}

	/**
	 * getHierarchicalData
	 * @param paramAsset
	 */
	public subscribeHierarchicalData(subLocation: string) {

		this.siteSubscriptions.hierarchicalData$.pipe(
			takeUntil(this.finalise), distinctUntilChanged()
		).subscribe({
			next: (data: string) => {
				if (data != "") {
					this.hierarchicalData = <IOrganisationsHierarchy[]>JSON.parse(data);
					this.locationAssets = [];
					this.locationParents = [];
					this.locationChildren = [];
					if (subLocation && (this.hierarchicalData.length > 0)) {
						this.fields = { dataSource: this.hierarchicalData, value: 'Uid', text: 'Name', child: 'SubChild' };
						this.hierarchicalLocation = this.organisationDataService.findObjectByUID(subLocation, this.hierarchicalData)
						/*** Route Found */
						if (this.hierarchicalLocation) {
							try {
								this.locationParents = this.organisationDataService.getParents(this.hierarchicalData[0], this.hierarchicalLocation.Uid);
								if (this.hierarchicalLocation.SubChild) {
									this.locationChildren = this.organisationDataService.getChildren(this.hierarchicalLocation);
									if (this.locationChildren) {
										for (const child of this.locationChildren) {
											let subAssets = <LevelAsset[]>this.assetsDataService.findAssets(child);
											let subwatches = <LevelAsset[]>this.assetsDataService.findAssets(child, true);
											let subActions = <LevelAsset[]>this.assetsDataService.findAssets(child, false, true);
											child.TotalAssetCount = subAssets.length;
											child.TotalWatchCount = subwatches.length;
											child.TotalActionCount = subActions.length;
										}
									}
								}
								if (this.hierarchicalLocation.Assets) {
									this.locationAssets = this.hierarchicalLocation.Assets;
								}
							}
							catch (error) { console.error(error); }
							this.selectedLocation = this.hierarchicalLocation.Name
						}
						else {
							// console.log("Route Not Found");
							/** Need to Reload the current URL */
						}
					}
					else { this.locationParents = [] }
				}
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.siteSubscriptions.hierarchicalData$", false) },
			//complete: () => {console.log("this.siteSubscriptions.hierarchicalData$.subscribe: Complete");}
		});
	}

	/**
	 * Bind the Select event
	 * @param e
	 */
	public dropdowntreeSelect(e: SelectEventArgs) {
		try {
			// Only process if initiated by user
			if (e.isInteracted) {
				let selNode: any = <string>e.itemData
				this.navigate(selNode.id);
			}
		} catch (error) { console.error(error); }
	}

	/**
	 * Bind the Select event
	 * @param asset
	 */
	public assetSelect(asset: ILevelAsset) {
		try {
			let selAsset: any = asset.AssetID;
			this.showAsset(selAsset);
		} catch (error) { console.error(error); }
	}

	/**
	 * Navigate to Asset
	 * @param assetID
	 */
	private showAsset(assetID: string) {
		try {
			// Create the new child route segment
			let currentUrl = this.router.url.split("/");
			let newChildSegment = currentUrl.slice(0, 5).join("/");
			// Navigate to the new URL with the changed child route
			this.router.navigate([`${newChildSegment}/asset/${assetID}`], { relativeTo: this.route.parent });
		} catch (error) { console.error(error); }
	}

	/**
	 * Bind the Select event
	 * @param location
	 */
	public locationSelect(location: IOrganisationsHierarchy) {
		try {
			let sellocation: any = location.Uid;
			this.navigate(sellocation);
		} catch (error) { console.error(error); }
	}

	/**
	 * Navigate to Sub Location
	 * @param locationUID
	 */
	private navigate(locationUID: string) {
		try {
			// Create the new child route segment
			let currentUrl = this.router.url.split("/");
			currentUrl[4] = locationUID;
			//let newChildSegment = 'view/' + selNode.id + "/" + this.childPath;
			// let newChildSegment = currentUrl.slice(0, currentUrl.length).join("/");
			let newChildSegment = currentUrl.slice(0, 6).join("/");
			// Navigate to the new URL with the changed child route
			this.router.navigate([newChildSegment], { relativeTo: this.route.parent });
			this.siteSubscriptions.SetSelectedSubLocation(locationUID)
		} catch (error) { console.error(error); }
	}

	/**
	 * Add ne w Asset
	 * @param subLocation
	 */
	addAsset(subLocation: OrganisationsHierarchy) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// Create a 'Local'/shallow copy of the Asset Information
		var _edit = <ILevelAsset>{};

		// Display the dialog box
		const dialogRef = this.dialog.open(EditHierarchyAssetComponent, {
			data: _edit,
		});

		// Update the original Asset with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			Object.assign(_edit, result);
			this.assetsDataService.postOrganisationAssets$(this.accountID, this.OrganisationID, subLocation.Uid, _edit).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (response) => {
					this.subscribeOrganisationStructure(this.accountID, this.OrganisationID)
				}
			});
		});

	}

	/**
	 * Add new location
	 * @param subLocation
	 */
	addLocation(subLocation: OrganisationsHierarchy) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// Create a 'Local'/shallow copy of the location Information
		var _edit = <IOrganisationsHierarchy>{};

		// Display the dialog box
		const dialogRef = this.dialog.open(EditHierarchyLocationComponent, {
			data: _edit,
		});

		// Update the original Location with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			Object.assign(_edit, result);
			this.organisationDataService.postOrganisationLocation$(this.accountID, this.OrganisationID, subLocation, _edit).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (response) => {
					this.subscribeOrganisationStructure(this.accountID, this.OrganisationID)
				},
				error: (error) => {
					if (error["status"] != 404) {
						this.globalFunctions.HandleError(error, "Error: this.organisationDataService.postOrganisationLocation.subscribe$", false)
					}
				},
			});
		});
	}

	/**
	 * Get the Organisation Structure
	 * @param AccountID AccountID
	 * @param OrganisationID OrganisationID
	 */
	subscribeOrganisationStructure(AccountID: string, OrganisationUID: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		try {
			/** Get the Organisation for the selection */
			this.organisationDataService.getOrganisationStructure$(AccountID, OrganisationUID).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((structure) => !!structure),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (structure: any) => {
					try {
						/** Update the Organisation */
						var OrganisationStructure = <IOrganisationsHierarchy[]>JSON.parse(structure);
						this.siteSubscriptions.SethierarchicalData(OrganisationStructure);
					} catch (error) { console.error(error); }
				},
				error: (error) => {
					if (error["status"] != 404) {
						this.globalFunctions.HandleError(error, "Error: OrganisationData.getOrganisationStructure$(OrganisationUID).subscribe$", false)
					}
				},
				//complete: () => { console.log("OrganisationData.getOrganisationStructure$(OrganisationUID).subscribe: Complete"); }
			});
		} catch (error) { console.error(error); }
	}

}
